import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, History } from "lucide-react";

const DOCS = [
  ["privacy", "Informativa Privacy"],
  ["cookie", "Cookie Policy"],
  ["terms", "Termini di Servizio"],
  ["dpa", "Data Processing Agreement (DPA)"],
  ["access_notice", "Informativa Richiesta Accesso"],
  ["pdf_module", "Modulo PDF (dichiarazione)"],
];

export default function AdminLegal({ lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [type, setType] = useState("privacy");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reaccept, setReaccept] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showHist, setShowHist] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true); setShowHist(false);
    try { const { data } = await api.get(`/legal/${t}`); setDoc(data); setReaccept(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(type); }, [type, load]);

  const setSection = (i, key, val) => setDoc({ ...doc, sections: doc.sections.map((s, j) => j === i ? { ...s, [key]: val } : s) });
  const addSection = () => setDoc({ ...doc, sections: [...(doc.sections || []), { heading: "", body: "" }] });
  const delSection = (i) => setDoc({ ...doc, sections: doc.sections.filter((_, j) => j !== i) });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/legal/${type}`, { title: doc.title, sections: doc.sections, requires_reacceptance: reaccept, bump_version: true });
      setDoc(data.document);
      toast.success(L(`Salvato — nuova versione v${data.document.version}`, `Saved — new version v${data.document.version}`));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const loadVersions = async () => {
    try { const { data } = await api.get(`/admin/legal/${type}/versions`); setVersions(data.versions); setShowHist(true); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-legal">
      <div className="flex flex-wrap gap-2 mb-5">
        {DOCS.map(([k, label]) => (
          <button key={k} data-testid={`legal-doc-${k}`} onClick={() => setType(k)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${type === k ? "bg-primary text-primary-foreground" : "border border-border hover:bg-accent"}`}>{label}</button>
        ))}
      </div>

      {loading || !doc ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : (
        <div className="max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-muted-foreground">v{doc.version} · {L("aggiornato", "updated")} {(doc.updated_at || "").slice(0, 10)}</span>
            <Button size="sm" variant="ghost" onClick={loadVersions} data-testid="legal-history"><History className="w-4 h-4 mr-1" />{L("Cronologia versioni", "Version history")}</Button>
          </div>

          {showHist && (
            <div className="mb-5 rounded-lg border border-border p-3 text-sm" data-testid="legal-versions">
              {versions.map((v) => <div key={v.id} className="flex justify-between py-1 border-b border-border/50 last:border-0"><span>v{v.version}</span><span className="text-muted-foreground">{(v.published_at || "").slice(0, 10)}</span></div>)}
            </div>
          )}

          <label className="text-xs text-muted-foreground">{L("Titolo", "Title")}</label>
          <Input className="mt-1 mb-5" value={doc.title || ""} onChange={(e) => setDoc({ ...doc, title: e.target.value })} data-testid="legal-title" />

          <div className="space-y-4">
            {(doc.sections || []).map((s, i) => (
              <div key={i} className="rounded-lg border border-border p-3" data-testid={`legal-section-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Input placeholder={L("Titolo sezione", "Section heading")} value={s.heading || ""} onChange={(e) => setSection(i, "heading", e.target.value)} data-testid={`legal-heading-${i}`} />
                  <button onClick={() => delSection(i)} className="p-2 text-red-500 hover:bg-muted rounded shrink-0" data-testid={`legal-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea rows={4} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder={L("Testo…", "Text…")} value={s.body || ""} onChange={(e) => setSection(i, "body", e.target.value)} data-testid={`legal-body-${i}`} />
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-3" onClick={addSection} data-testid="legal-add-section"><Plus className="w-4 h-4 mr-1" />{L("Aggiungi sezione", "Add section")}</Button>

          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <Button onClick={save} disabled={busy} data-testid="legal-save">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{L("Pubblica nuova versione", "Publish new version")}</Button>
            {["privacy", "terms", "dpa", "cookie"].includes(type) && (
              <label className="flex items-center gap-2 text-sm" data-testid="legal-reaccept-label">
                <input type="checkbox" checked={reaccept} onChange={(e) => setReaccept(e.target.checked)} data-testid="legal-reaccept" />
                {L("Richiedi nuova accettazione agli utenti", "Require users to re-accept")}
              </label>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">{L("Placeholder disponibili: {{ragione_sociale}}, {{legal_address}}, {{vat_code}}, {{support_email}} — sostituiti automaticamente coi dati Azienda.", "Available placeholders: {{ragione_sociale}}, {{legal_address}}, {{vat_code}}, {{support_email}} — auto-filled from Company info.")}</p>
        </div>
      )}
    </div>
  );
}
